import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const TTL_MS = 10 * 24 * 60 * 60 * 1000;

export interface SupplyNotificationItem {
  id: string;
  subject: string;
  from: string;
  receivedDateTime?: string;
  category: "new_order" | "delivery_issue" | "delivery_followup" | "other";
  label: string;
  orderReference?: string | null;
  externalId?: string | null;
  confidence?: number;
  summary?: string;
  highlights?: string[];
  entityName?: string;
  addedAt: string;
}

export interface SupplyNotificationsState {
  items: SupplyNotificationItem[];
}

const initialState: SupplyNotificationsState = {
  items: [],
};

const pruneExpired = (items: SupplyNotificationItem[], now = Date.now()) =>
  items.filter((item) => now - new Date(item.addedAt).getTime() <= TTL_MS);

const sortByRecency = (items: SupplyNotificationItem[]) =>
  items
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.receivedDateTime || a.addedAt).getTime();
      const bTime = new Date(b.receivedDateTime || b.addedAt).getTime();
      return bTime - aTime;
    });

const supplyNotificationsSlice = createSlice({
  name: "supplyNotifications",
  initialState,
  reducers: {
    setSupplyNotifications(state, action: PayloadAction<SupplyNotificationItem[]>) {
      state.items = sortByRecency(pruneExpired(action.payload));
    },
    upsertSupplyNotifications(state, action: PayloadAction<SupplyNotificationItem[]>) {
      const now = Date.now();
      const byId = new Map(state.items.map((item) => [item.id, item]));
      action.payload.forEach((item) => {
        const existing = byId.get(item.id);
        const addedAt = item.addedAt || existing?.addedAt || new Date().toISOString();
        byId.set(item.id, { ...existing, ...item, addedAt });
      });
      state.items = sortByRecency(pruneExpired(Array.from(byId.values()), now));
    },
    pruneSupplyNotifications(state) {
      state.items = sortByRecency(pruneExpired(state.items));
    },
    removeSupplyNotifications(state, action: PayloadAction<string[]>) {
      const ids = new Set(action.payload.map((id) => String(id)));
      if (!ids.size) return;
      state.items = state.items.filter((item) => !ids.has(item.id));
    },
    clearSupplyNotifications(state) {
      state.items = [];
    },
  },
});

export const {
  setSupplyNotifications,
  upsertSupplyNotifications,
  pruneSupplyNotifications,
  removeSupplyNotifications,
  clearSupplyNotifications,
} = supplyNotificationsSlice.actions;

export default supplyNotificationsSlice.reducer;
